import { CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react"

// The app's one popup menu (dropdown-menu + dropdown-item): the "…" overflow
// menu of a row with more actions than fit as links, the subscription
// banner's menu, the installer picker when a product has several skus. The
// trigger is the "…" btn-more by default; children + className/style make
// it any other button (a download link). Opens downward, right-aligned to
// the trigger, and flips when the menu would leave the viewport: upward
// when rows at the end of a list would push it below the fold of the
// scrolling .app-content, left-aligned when a trigger near the left edge
// (narrow screens) would push it off the side. Closes on an outside click,
// Escape or a pick. Clicks never bubble past it — menus sit on clickable
// rows and banners.
export interface MoreMenuItem {
  label: string
  // a link item (an installer, the store's payment page) — opens in a new
  // tab when external; otherwise a button
  href?: string
  external?: boolean
  onClick?: () => void
  danger?: boolean
}

export default function MoreMenu({ items, label = "Actions", className = "btn-more", style, children }: {
  items: MoreMenuItem[]
  // the trigger's accessible name (the default "…" has no text)
  label?: string
  className?: string
  style?: CSSProperties
  children?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const [up, setUp] = useState(false)
  const [alignLeft, setAlignLeft] = useState(false)
  const wrapperRef = useRef<HTMLSpanElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false) }
    document.addEventListener("mousedown", handleClickOutside)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
      document.removeEventListener("keydown", handleKey)
    }
  }, [open])

  // measured after the menu renders (before paint) so it never flashes in
  // the wrong place: below and right-aligned by default; above when below
  // would overflow the viewport and above fits; left-aligned when
  // right-aligned would cross the left edge and left-aligned fits
  useLayoutEffect(() => {
    if (!open) { setUp(false); setAlignLeft(false); return }
    const wrapper = wrapperRef.current
    const menu = menuRef.current
    if (!wrapper || !menu) return
    const anchor = wrapper.getBoundingClientRect()
    const { height: menuHeight, width: menuWidth } = menu.getBoundingClientRect()
    const overflowsBelow = anchor.bottom + 6 + menuHeight > window.innerHeight
    const fitsAbove = anchor.top - 6 - menuHeight >= 0
    setUp(overflowsBelow && fitsAbove)
    const overflowsLeft = anchor.right - menuWidth < 0
    const fitsRightward = anchor.left + menuWidth <= window.innerWidth
    setAlignLeft(overflowsLeft && fitsRightward)
  }, [open, items.length])

  const menuClass = `dropdown-menu${up ? " dropdown-menu-up" : ""}${alignLeft ? " dropdown-menu-left" : ""}`

  return (
    <span ref={wrapperRef} style={{ position: "relative", display: "inline-block" }} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={className}
        style={style}
        aria-label={children === undefined ? label : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
      >
        {children ?? <>&#x2026;</>}
      </button>
      {open && (
        <div ref={menuRef} className={menuClass} role="menu">
          {items.map((item) => {
            const itemClass = `dropdown-item${item.danger ? " dropdown-item-danger" : ""}`
            const pick = () => { setOpen(false); item.onClick?.() }
            return item.href !== undefined ? (
              <a
                key={item.label}
                href={item.href}
                className={itemClass}
                role="menuitem"
                target={item.external ? "_blank" : undefined}
                rel={item.external ? "noopener noreferrer" : undefined}
                onClick={pick}
              >
                {item.label}
              </a>
            ) : (
              <button key={item.label} type="button" className={itemClass} role="menuitem" onClick={pick}>
                {item.label}
              </button>
            )
          })}
        </div>
      )}
    </span>
  )
}
